import React, { memo, useMemo } from "react";
import { Zap, GripVertical, FolderOpen, User as UserIcon, Users, Wrench } from "lucide-react";
import { cn } from "../../utils/cn";
import { effortOptions } from "../../utils/constants";
import { isEpicStalled, getLastActivityLabel } from "../../utils/velocity";
import { LanePill } from "../ui/LanePill";
import { TrendBadge } from "../ui/TrendBadge";
import { Epic, Project, User, UserRole } from "../../types";

/**
 * Status gradient colors for lane accent bar
 */
const statusColors: Record<string, string> = {
    Now: "from-emerald-500 via-emerald-400 to-teal-400",
    Next: "from-amber-500 via-orange-400 to-yellow-400",
    Later: "from-blue-500 via-indigo-400 to-purple-400",
    Unassigned: "from-slate-400 via-slate-300 to-slate-400",
    Done: "from-green-600 via-emerald-500 to-teal-500"
};

interface EpicCardProps {
    epic: Epic;
    onClick?: (e: React.MouseEvent) => void;
    selected?: boolean;
    previousScore?: number | null;
    compact?: boolean;
    dragging?: boolean;
    projectName?: string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: UserRole;
}

/**
 * Avatar component for displaying user
 */
const UserAvatar = memo(function UserAvatar({
    user,
    size = 'sm',
    showTooltip = true
}: {
    user: User;
    size?: 'xs' | 'sm' | 'md';
    showTooltip?: boolean;
}) {
    const sizeClasses = {
        xs: 'w-5 h-5 text-[8px]',
        sm: 'w-6 h-6 text-[10px]',
        md: 'w-8 h-8 text-xs'
    };

    const initials = user.displayName
        ?.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || '?';

    if (user.photoUrl) {
        return (
            <img
                src={user.photoUrl}
                alt={user.displayName}
                title={showTooltip ? user.displayName : undefined}
                className={cn(
                    sizeClasses[size],
                    "rounded-full object-cover border-2 border-white dark:border-slate-800 shadow-sm",
                    "ring-1 ring-black/5 dark:ring-white/10"
                )}
            />
        );
    }

    return (
        <div
            title={showTooltip ? user.displayName : undefined}
            className={cn(
                sizeClasses[size],
                "rounded-full flex items-center justify-center font-bold",
                "bg-gradient-to-br from-siemens-petrol to-siemens-petrol-dark",
                "text-white border-2 border-white dark:border-slate-800 shadow-sm",
                "ring-1 ring-black/5 dark:ring-white/10"
            )}
        >
            {initials}
        </div>
    );
});

/**
 * User group display with avatars
 */
const UserGroup = memo(function UserGroup({
    users,
    label,
    icon: Icon,
    emptyText = "Not assigned",
    maxDisplay = 3
}: {
    users: User[];
    label: string;
    icon: React.ElementType;
    emptyText?: string;
    maxDisplay?: number;
}) {
    if (users.length === 0) {
        return (
            <div className="flex items-center gap-2 py-1">
                <div className="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Icon className="w-3 h-3 text-slate-400 dark:text-slate-500" />
                </div>
                <div className="flex flex-col">
                    <span className="text-[9px] font-medium text-siemens-text-soft uppercase tracking-wider">{label}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 italic">{emptyText}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 py-1">
            <div className="flex -space-x-1.5">
                {users.slice(0, maxDisplay).map((user, idx) => (
                    <div key={user.id} style={{ zIndex: maxDisplay - idx }} className="relative">
                        <UserAvatar user={user} size="sm" />
                    </div>
                ))}
                {users.length > maxDisplay && (
                    <div
                        className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-300 border-2 border-white dark:border-slate-800 shadow-sm z-0"
                        title={users.slice(maxDisplay).map(u => u.displayName).join(', ')}
                    >
                        +{users.length - maxDisplay}
                    </div>
                )}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="text-[9px] font-medium text-siemens-text-soft uppercase tracking-wider">{label}</span>
                <span className="text-xs font-medium text-siemens-deep-blue dark:text-siemens-text-standard truncate">
                    {users.length === 1
                        ? users[0].displayName
                        : `${users.length} assigned`}
                </span>
            </div>
        </div>
    );
});

/**
 * EpicCard component - Beautiful, sleek, and fully functional
 * Memoized to prevent unnecessary re-renders when epic data hasn't changed
 */
export const EpicCard = memo(function EpicCard({
    epic,
    onClick,
    selected,
    previousScore,
    compact = false,
    dragging = false,
    projectName,
    projects,
    onUpdate,
    users,
    userRole
}: EpicCardProps) {
    // Determine if user can change project (Owner role cannot)
    const canChangeProject = onUpdate && userRole !== 'owner';
    // Get assigned service owners
    const assignedOwners = useMemo(() => {
        if (!users || !epic.so) return [];
        const soArray = Array.isArray(epic.so) ? epic.so : [epic.so];
        return soArray
            .map(id => users.find(u => u.id === id))
            .filter(Boolean) as User[];
    }, [epic.so, users]);

    // Get assigned architects
    const assignedArchitects = useMemo(() => {
        if (!users || !epic.arq) return [];
        const arqArray = Array.isArray(epic.arq) ? epic.arq : [epic.arq];
        return arqArray
            .map(id => users.find(u => u.id === id))
            .filter(Boolean) as User[];
    }, [epic.arq, users]);

    // Memoize effort lookup
    const effortOption = useMemo(() =>
        effortOptions.find((x) => x.value === epic.effort) || { label: "S", color: "bg-slate-100" },
        [epic.effort]
    );

    // Memoize computed status color
    const statusColor = useMemo(() =>
        statusColors[epic.lane] || statusColors.Unassigned,
        [epic.lane]
    );

    // Memoize container classes
    const containerClasses = useMemo(() => cn(
        "group w-full rounded-2xl text-left transition-all duration-300 cursor-pointer relative overflow-hidden",
        "bg-white dark:bg-slate-900/80",
        "border border-slate-200/80 dark:border-slate-700/50",
        "shadow-lg shadow-slate-200/50 dark:shadow-none",
        "hover:shadow-xl hover:shadow-slate-300/50 dark:hover:shadow-none",
        "hover:-translate-y-0.5 hover:border-siemens-petrol/30 dark:hover:border-siemens-petrol/50",
        "backdrop-blur-sm",
        selected && "ring-2 ring-siemens-petrol ring-offset-2 ring-offset-white dark:ring-offset-slate-950 border-siemens-petrol/50",
        dragging && "opacity-50 scale-[0.98] rotate-2 shadow-2xl"
    ), [selected, dragging]);

    return (
        <div onClick={onClick} className={containerClasses}>
            {/* Animated gradient accent bar */}
            <div className={cn(
                "absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r",
                statusColor,
                "group-hover:h-2 transition-all duration-300"
            )} />

            {/* Card Content */}
            <div className="p-4 pt-5">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative group/lane z-10" onClick={(e) => e.stopPropagation()}>
                            <LanePill lane={epic.lane} />
                            {onUpdate && (
                                <select
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    value={epic.lane}
                                    onChange={(e) => {
                                        const newLane = e.target.value;
                                        const updates: any = { lane: newLane };
                                        // Moving to Done → set progress to 100%
                                        if (newLane === 'Done' && epic.lane !== 'Done') {
                                            updates.progress = 100;
                                            updates.completedAt = new Date().toISOString();
                                        }
                                        // Moving from Done → reset progress to 0%
                                        if (newLane !== 'Done' && epic.lane === 'Done') {
                                            updates.progress = 0;
                                            updates.completedAt = undefined;
                                        }
                                        onUpdate(epic.id, updates);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="Unassigned">Unassigned</option>
                                    <option value="Now">Now</option>
                                    <option value="Next">Next</option>
                                    <option value="Later">Later</option>
                                    <option value="Done">Done</option>
                                </select>
                            )}
                        </div>

                        {epic.sgm && (
                            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-siemens-petrol to-siemens-petrol-dark text-white shadow-sm">
                                SGM
                            </span>
                        )}

                        {/* Stalled Indicator */}
                        {isEpicStalled(epic) && (
                            <span
                                className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-sm animate-pulse cursor-help"
                                title={`Last updated: ${getLastActivityLabel(epic)}`}
                            >
                                ⚠ Stalled
                            </span>
                        )}

                        {/* Project Badge */}
                        {projectName && (
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                                <button className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-siemens-petrol/10 hover:text-siemens-petrol transition-all duration-200">
                                    <FolderOpen className="w-3 h-3" />
                                    <span className="truncate max-w-[80px]">{projectName}</span>
                                </button>

                                {canChangeProject && projects && (
                                    <select
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        value={epic.projectId || 'project-legacy'}
                                        onChange={(e) => onUpdate!(epic.id, { projectId: e.target.value })}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <option value="project-legacy">Legacy</option>
                                        {projects.map(p => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        )}
                    </div>

                    {/* WSJF Score - Premium look */}
                    <div className="flex items-center gap-2">
                        <TrendBadge current={epic.wsjf} previous={previousScore} />
                        <div className="flex flex-col items-end">
                            <span className="text-3xl font-black bg-gradient-to-br from-siemens-deep-blue to-siemens-petrol bg-clip-text text-transparent dark:from-white dark:to-siemens-petrol-light tracking-tighter tabular-nums leading-none">
                                {(epic.wsjf || 0).toFixed(1)}
                            </span>
                            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">WSJF</span>
                        </div>
                    </div>
                </div>

                {/* Title */}
                <h3 className="text-sm font-semibold leading-snug text-siemens-deep-blue dark:text-white group-hover:text-siemens-petrol dark:group-hover:text-siemens-petrol-light transition-colors duration-300 line-clamp-2 mb-2">
                    {epic.title}
                </h3>

                {/* Progress Bar */}
                {typeof epic.progress === 'number' && (
                    <div className="mb-3">
                        <div className="flex items-center justify-between text-[10px] mb-1">
                            <span className="font-medium text-slate-500">Progress</span>
                            <span className="font-bold text-siemens-petrol dark:text-siemens-petrol-light">{epic.progress}%</span>
                        </div>
                        <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                                className={cn(
                                    "h-full rounded-full transition-all duration-500",
                                    epic.progress === 100
                                        ? "bg-gradient-to-r from-green-500 to-emerald-400"
                                        : epic.progress >= 50
                                            ? "bg-gradient-to-r from-siemens-petrol to-teal-400"
                                            : "bg-gradient-to-r from-amber-500 to-orange-400"
                                )}
                                style={{ width: `${epic.progress}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Metrics & People Section */}
                {!compact && (
                    <div className="pt-3 border-t border-slate-100 dark:border-slate-800">
                        {/* People Row */}
                        <div className="flex items-center justify-between gap-4 mb-3">
                            <UserGroup
                                users={assignedOwners}
                                label="Owners"
                                icon={Users}
                                emptyText="TBD"
                            />
                            <UserGroup
                                users={assignedArchitects}
                                label="Architects"
                                icon={Wrench}
                                emptyText="TBD"
                            />
                        </div>

                        {/* Metrics Row */}
                        <div className="flex items-center justify-between">
                            {/* CoD Badge */}
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-200/50 dark:shadow-none">
                                    <Zap className="w-4 h-4 text-white" fill="currentColor" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[9px] font-medium text-slate-400 uppercase tracking-wider">CoD</span>
                                    <span className="text-lg font-black text-siemens-deep-blue dark:text-white tabular-nums leading-none">
                                        {(epic.cod || 0).toFixed(0)}
                                    </span>
                                </div>
                            </div>

                            {/* Effort Badge - Premium style */}
                            <div className={cn(
                                "px-4 py-2 rounded-xl text-sm font-black",
                                "bg-gradient-to-br from-siemens-deep-blue to-slate-800",
                                "text-white shadow-lg shadow-siemens-deep-blue/25 dark:shadow-none",
                                "ring-1 ring-white/20"
                            )}>
                                {effortOption.label}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Subtle shine effect on hover */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

            {/* Drag Handle */}
            <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-40 transition-opacity duration-300">
                <GripVertical className="w-4 h-4 text-slate-400" />
            </div>
        </div>
    );
});

export default EpicCard;
